import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import { FormControl, FormField, FormHelper, FormItem } from '@paid-ui/components/form';
import { RadioGroup, RadioGroupCard } from '@paid-ui/components/radio-group';
import { AlertIcon } from '@paid-ui/icons/alert';

import { signUpManager } from '../../models';
import { OwnerType, SignUpStep } from '../../models/enums';
import { resetBusinessFields } from '../../utils/reset-business-fields';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

export const StepOwnerType: React.FC<Props> = ({ disabled, goToStep }) => {
  const form = useFormContext();
  const { invitedSignUp, partyType, currentStep } = useSnapshot(signUpManager);

  const ownerType = form.watch('ownerType') as OwnerType;
  const isValid = Boolean(ownerType);

  const handleNext = () => {
    switch (ownerType) {
      case OwnerType.INDIVIDUALS: {
        resetBusinessFields(form);

        if (invitedSignUp && partyType === OwnerType.BUSINESS) {
          goToStep?.(SignUpStep.OWNER_CONFIRM);
        } else {
          goToStep?.(SignUpStep.USER_DETAILS);
        }
        break;
      }
      case OwnerType.BUSINESS: {
        goToStep?.(SignUpStep.BUSINESS_STRUCTURE);
        break;
      }
      default:
    }
  };

  if (currentStep !== SignUpStep.OWNER_TYPE) {
    return null;
  }

  if (invitedSignUp) {
    switch (partyType) {
      case OwnerType.INDIVIDUALS: {
        return (
          <div className="space-y-9">
            <div className="space-y-3.5">
              <FormField
                name="ownerType"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        {...field}
                        onValueChange={(value) =>
                          form.setValue('ownerType', value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={disabled}
                        className="space-y-3.5"
                      >
                        <RadioGroupCard key={OwnerType.INDIVIDUALS} value={OwnerType.INDIVIDUALS}>
                          Ok, join as an individual
                        </RadioGroupCard>
                        <RadioGroupCard key={OwnerType.BUSINESS} value={OwnerType.BUSINESS}>
                          No, I want to join as a business.
                        </RadioGroupCard>
                        {ownerType === OwnerType.BUSINESS ? (
                          <div className="grid grid-cols-[auto_1fr] gap-x-3.5 rounded-[10px] border border-pink p-5 text-sm text-pink">
                            <AlertIcon className="my-1 h-5 w-5" />
                            <div>
                              You've been invited to join Paid as an individual. Switching to a
                              business sets how you're represented on contracts and can't be changed
                              later.
                            </div>
                          </div>
                        ) : null}
                      </RadioGroup>
                    </FormControl>
                    <FormHelper />
                  </FormItem>
                )}
              />
            </div>
            <Button
              variant="solid"
              disabled={disabled || !isValid}
              className="h-[44px]"
              onClick={handleNext}
            >
              Continue
            </Button>
          </div>
        );
      }
      case OwnerType.BUSINESS: {
        return (
          <div className="space-y-9">
            <div className="space-y-3.5">
              <FormField
                name="ownerType"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        {...field}
                        onValueChange={(value) =>
                          form.setValue('ownerType', value, {
                            shouldValidate: true,
                          })
                        }
                        disabled={disabled}
                        className="space-y-3.5"
                      >
                        <RadioGroupCard key={OwnerType.BUSINESS} value={OwnerType.BUSINESS}>
                          Ok, join as a business.
                        </RadioGroupCard>
                        <RadioGroupCard key={OwnerType.INDIVIDUALS} value={OwnerType.INDIVIDUALS}>
                          No, I want to join as an individual.
                        </RadioGroupCard>
                        {ownerType === OwnerType.INDIVIDUALS ? (
                          <div className="grid grid-cols-[auto_1fr] gap-x-3.5 rounded-[10px] border border-pink p-5 text-sm text-pink">
                            <AlertIcon className="my-1 h-5 w-5" />
                            <div>
                              You've been invited to join Paid as a business. Switching to an
                              individual sets how you're represented on contracts and can't be
                              changed later.
                            </div>
                          </div>
                        ) : null}
                      </RadioGroup>
                    </FormControl>
                    <FormHelper />
                  </FormItem>
                )}
              />
            </div>
            <Button
              variant="solid"
              disabled={disabled || !isValid}
              className="h-[44px]"
              onClick={handleNext}
            >
              Continue
            </Button>
          </div>
        );
      }
      default:
    }
  }

  return (
    <div className="space-y-9">
      <div className="space-y-3.5">
        <FormField
          name="ownerType"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup
                  {...field}
                  onValueChange={(value) =>
                    form.setValue('ownerType', value, {
                      shouldValidate: true,
                    })
                  }
                  disabled={disabled}
                  className="space-y-3.5"
                >
                  <RadioGroupCard value={OwnerType.BUSINESS}>Yes, I have a business</RadioGroupCard>
                  <RadioGroupCard value={OwnerType.INDIVIDUALS}>
                    No, I don't have a business
                  </RadioGroupCard>
                </RadioGroup>
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
      </div>
      <Button
        variant="solid"
        disabled={disabled || !isValid}
        className="h-[44px]"
        onClick={handleNext}
      >
        Continue
      </Button>
    </div>
  );
};
